import React, { useState, useEffect, useRef, useContext } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import CircularProgress from '@mui/material/CircularProgress';
import Layout from '../../components/Layout';
import { TextField, Tooltip, Button, Checkbox, FormControlLabel, Select, InputLabel, MenuItem, Grid, Box, Alert, Snackbar, Dialog, DialogActions, DialogTitle, DialogContent, Typography, Icon, IconButton, FormControl, useTheme, useMediaQuery, Card, CardContent, Stack, Accordion, AccordionSummary, AccordionDetails, Chip } from '@mui/material';
import axios from 'axios';
import HelpIcon from '@mui/icons-material/Help';
import { AuthContext } from '../../AuthContext';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';

const UsersGrid = () => {
    const [rows, setRows] = useState([]);
    const [gridHeight, setGridHeight] = useState(400);  // default value
    const [loadingAddOrUpdate, setLoadingAddOrUpdate] = useState(false);
    const [deletingIds, setDeletingIds] = useState(new Set());
    const [gridLoading, setGridLoading] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [editedEntry, setEditedEntry] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [partnerDeleteDialogOpen, setPartnerDeleteDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [initialSelectedKiosks, setInitialSelectedKiosks] = useState(new Set());
    const { user } = useContext(AuthContext);

    const [kiosks, setKiosks] = useState([]);
    const [selectedKiosks, setSelectedKiosks] = useState(new Set());
    const [selectedKioskAdmins, setSelectedKioskAdmins] = useState(new Set());
    const [selectedVideoAccess, setSelectedVideoAccess] = useState(new Set());
    const [selectedMovementNotifications, setSelectedMovementNotifications] = useState(new Set());
    const [selectedReviewVideoMessages, setSelectedReviewVideoMessages] = useState(new Set());
    const [selectedOnlyAllowQRCodeUnlock, setSelectedOnlyAllowQRCodeUnlock] = useState(new Set());

    const [kioskSelectionError, setKioskSelectionError] = useState('');

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    useEffect(() => {
        //console.log('Refreshing user list and tenants');
        fetchData();

    }, []);
    
    const roleOptions = [
        { value: 'CLIENT_ADMIN', label: 'Admin' },
        { value: 'CLIENT_USER', label: 'User' }
      ];

      
    const fetchTenants = async () => {
        //console.log('fetching tenants')
        await axios.get('/api/kiosks')
        .then(result => {
            setKiosks(result.data);
        })
        .catch(error => {
            console.error('Error fetching kiosks:', error);
        });
    };

    useEffect(() => {
        const viewportHeight = window.innerHeight;
        const calculatedHeight = viewportHeight - 150;
        
        setGridHeight(calculatedHeight);
    }, []); 
    
    // State for form inputs
    const [form, setForm] = useState({
        email: '', first_name: '', last_name: '', role: 'CLIENT_USER'
    });
    
    // Function to handle input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setForm(prevForm => ({
            ...prevForm,
            [name]: value,
        }));
    };

    // Function to handle kiosk checkbox changes
    const handleKioskCheckboxChange = (e) => {
        setKioskSelectionError('');
        const kioskId = Number(e.target.name); // Convert to number
        setSelectedKiosks(prevSelectedKiosks => {
            const newSelectedKiosks = new Set(prevSelectedKiosks);
            const isChecked = e.target.checked;
    
            if (isChecked) {
                newSelectedKiosks.add(kioskId);
            } else {
                newSelectedKiosks.delete(kioskId);
                // If Responder is unticked, untick Admin, Allow Adhoc Video, and Movement Notifications
                if (selectedKioskAdmins.has(kioskId)) {
                    setSelectedKioskAdmins(prev => {
                        const updated = new Set(prev);
                        updated.delete(kioskId);
                        return updated;
                    });
                }
                if (selectedVideoAccess.has(kioskId)) {
                    setSelectedVideoAccess(prev => {
                        const updated = new Set(prev);
                        updated.delete(kioskId);
                        return updated;
                    });
                }
                if (selectedMovementNotifications.has(kioskId)) {
                    setSelectedMovementNotifications(prev => {
                        const updated = new Set(prev);
                        updated.delete(kioskId);
                        return updated;
                    });
                }
                if (selectedReviewVideoMessages.has(kioskId)) {
                    setSelectedReviewVideoMessages(prev => {
                        const updated = new Set(prev);
                        updated.delete(kioskId);
                        return updated;
                    });
                }
                if (selectedOnlyAllowQRCodeUnlock.has(kioskId)) {
                    setSelectedOnlyAllowQRCodeUnlock(prev => {
                        const updated = new Set(prev);
                        updated.delete(kioskId);
                        return updated;
                    });
                }
                
            }
            return newSelectedKiosks;
        });
    };
    
    
    const handleKioskAdminCheckboxChange = (event) => {
        const kioskId = parseInt(event.target.name.split("-")[1], 10); 
        setSelectedKioskAdmins(prevSelectedKioskAdmins => {
            const newSelectedKioskAdmins = new Set(prevSelectedKioskAdmins);
            if (event.target.checked) {
                newSelectedKioskAdmins.add(kioskId);
            } else {
                newSelectedKioskAdmins.delete(kioskId);
            }
            return newSelectedKioskAdmins;
        });
    };
    
    const handleVideAccessCheckboxChange = (event) => {
        const kioskId = parseInt(event.target.name.split("-")[1], 10); 
        setSelectedVideoAccess(preSelectedVideoAccess => {
            const newSelectedVideoAccess = new Set(preSelectedVideoAccess);
            if (event.target.checked) {
                newSelectedVideoAccess.add(kioskId);
            } else {
                newSelectedVideoAccess.delete(kioskId);
            }
            return newSelectedVideoAccess;
        });
    };

    const handleMovementNotificationsCheckboxChange = (event) => {
        const kioskId = parseInt(event.target.name.split("-")[1], 10); 
        setSelectedMovementNotifications(preSelectedMovementNotifications => {
            const newAccess = new Set(preSelectedMovementNotifications);
            if (event.target.checked) {
                newAccess.add(kioskId);
            } else {
                newAccess.delete(kioskId);
            }
            return newAccess;
        });
    };
    
    const handleReviewVideoMessagesCheckboxChange = (event) => {
        const kioskId = parseInt(event.target.name.split("-")[1], 10); 
        setSelectedReviewVideoMessages(preSelectedReviewVideoMessages => {
            const newAccess = new Set(preSelectedReviewVideoMessages);
            if (event.target.checked) {
                newAccess.add(kioskId);
            } else {
                newAccess.delete(kioskId);
            }
            return newAccess;
        });
    };

    const handleOnlyAllowQRCodeUnlockCheckboxChange = (event) => {
        const kioskId = parseInt(event.target.name.split("-")[1], 10); 
        setSelectedOnlyAllowQRCodeUnlock(prevSelectedOnlyAllowQRCodeUnlock => {
            const newAccess = new Set(prevSelectedOnlyAllowQRCodeUnlock);
            if (event.target.checked) {
                newAccess.add(kioskId);
            } else {
                newAccess.delete(kioskId);
            }
            return newAccess;
        });
    };

    // Function to add responder (assuming that you have a User ID)
    // Convert addResponder to an async function
    const addResponder = async (userId, tenantId, IsKioskAdmin, allowAdhocVideoAccess, AllowMovementNotifications, ReviewVideoMessages, OnlyAllowQRCodeUnlock) => {
        try {
            const response = await axios.post('/api/userResponders', { 
                UserID: userId, 
                TenantID: tenantId, 
                IsKioskAdmin: IsKioskAdmin, 
                AllowAdhocVideoAccess: IsKioskAdmin || allowAdhocVideoAccess,
                AllowMovementNotifications: IsKioskAdmin || AllowMovementNotifications,
                ReviewVideoMessages: IsKioskAdmin || ReviewVideoMessages,
                OnlyAllowQRCodeUnlock: OnlyAllowQRCodeUnlock
            });
            //console.log('Responder added:', response.data);
        } catch (error) {
            console.error('Error adding responder:', error);
            throw error; // Rethrow the error so it can be caught by the caller
        }
    };
    
    // Function to delete responder (assuming that you have a User ID)
    const deleteResponder = async (userId, tenantId) => {
        await axios.delete(`/api/userResponders`, { params: { userId, tenantId } })
        .then(response => {
            //console.log('Responder deleted:', response.data);
        })
        .catch(error => {
            console.error('Error deleting responder:', error);
        });
    };
    
    
    // Function to handle form submission
    const handleAddSubmit = async (e) => {
        e.preventDefault();
    
        if (selectedKiosks.size === 0 && form.role === 'CLIENT_USER') {
            setKioskSelectionError('You have not selected any kiosks. This means the user will not have access to anything. You cannot proceed unless you select at least one kiosk.');
            return;
        }
    
        setKioskSelectionError('');
        setLoadingAddOrUpdate(true);
    
        try {
            // Check responder limit before adding user
            const limitCheck = await axios.get('/api/checkResponderLimit');
            if (!limitCheck.data.allowed) {
                setErrorMessage(limitCheck.data.reason);
                setShowAlert(true);
                setLoadingAddOrUpdate(false);
                return;
            }

            const data = await axios.post('/api/users', form);
            const newUserId = data.data.id;
        
            if (form.role === 'CLIENT_USER') {
                const kiosksToAdd = Array.from(selectedKiosks);
        
                for (const tenantId of kiosksToAdd) {
                    //console.log('About to add ' + tenantId);
                    const isAdmin = selectedKioskAdmins.has(tenantId);
                    const isVideoAccess = selectedVideoAccess.has(tenantId);
                    const isMovementAccess = selectedMovementNotifications.has(tenantId);
                    const isReviewVideoMessages = selectedReviewVideoMessages.has(tenantId);
                    const isOnlyAllowQRCodeUnlock = selectedOnlyAllowQRCodeUnlock.has(tenantId);
                    
                    try {
                        await addResponder(newUserId, tenantId, isAdmin, isVideoAccess, isMovementAccess, isReviewVideoMessages, isOnlyAllowQRCodeUnlock);
                    } catch (error) {
                        console.log(error);
                        setLoadingAddOrUpdate(false);
                        setErrorMessage(error.response.data);
                        setShowAlert(true);
                        
                    }
                }
            }
            else
            {
                await addAdminResponderToAllKiosks(newUserId);
               
                
            }
    
            setSuccessMessage('User added successfully. They will receive an email with a link to set their password.');
            setShowSuccess(true);
            setDialogOpen(false);
            resetForm();
            fetchData();
        } catch (error) {
            console.log(error);
            setErrorMessage(error.response.data);
            setShowAlert(true);
        } finally {
            setLoadingAddOrUpdate(false);
        }
    };
    
    const addAdminResponderToAllKiosks = async (userId) => {

        // add kiosk admin to ALL kiosks
        for (const tenant of kiosks) {
            //console.log('About to add admin ' + tenant.id);
            
            try {
                await addResponder(userId, tenant.id, true, false, true, false, false);
            } catch (error) {
                console.log(error);
                setLoadingAddOrUpdate(false);
                setErrorMessage(error.response.data);
                setShowAlert(true);
                
            }
        }
    }

    const fetchUserResponders = (userId) => {
        axios.get(`/api/userResponders?id=${userId}`)
        .then(result => {
            const existingKioskIds = new Set(result.data.map(r => r.TenantID));
            setSelectedKiosks(existingKioskIds);
            setInitialSelectedKiosks(new Set(existingKioskIds));
            setSelectedKioskAdmins(new Set(result.data.filter(r => r.IsKioskAdmin).map(r => r.TenantID)));
            setSelectedVideoAccess(new Set(result.data.filter(r => r.AllowAdhocVideoAccess).map(r => r.TenantID)));
            setSelectedMovementNotifications(new Set(result.data.filter(r => r.AllowMovementNotifications).map(r => r.TenantID)));
            setSelectedReviewVideoMessages(new Set(result.data.filter(r => r.ReviewVideoMessages).map(r => r.TenantID)));
            setSelectedOnlyAllowQRCodeUnlock(new Set(result.data.filter(r => r.OnlyAllowQRCodeUnlock).map(r => r.TenantID)));

        })
        .catch(error => {
            console.error('Error fetching user responders:', error);
        });
    };
    
    const fetchData = () => {
        setGridLoading(true);
        axios.get('/api/users')
        .then(result => {
            let data = result.data.filter(user => user.role !== 'DEVELOPER'); // Filter out DEVELOPER users
            
            setGridLoading(false);
            setRows(data);
            
        })
        .catch(error => {
            setGridLoading(false);
            console.error('Error fetching data:', error);
            setShowAlert(true);
        });
    };



    // Function to handle editing
    const handleEditClick = async (entry) => {
        await fetchTenants();
        setForm({ email: entry.email, first_name: entry.first_name, last_name: entry.last_name, role: entry.role });
        // get the user responders
        fetchUserResponders(entry.id);
        setIsEditing(true);
        setEditedEntry(entry);
        setKioskSelectionError('');
        setDialogOpen(true); // Open the dialog directly without clearing permissions
    };

    
    // Function to handle update submission
   // Update handleUpdateSubmit
    const handleUpdateSubmit = async (e) => {
        e.preventDefault();
        setLoadingAddOrUpdate(true);

        try {

            // check if number of responders will be breached
            const data = await axios.put(`/api/users/${editedEntry.id}`, form);
            let errorOccurred = false;

            if (form.role == 'CLIENT_USER') { //only do this for client_user

                // Delete all initially selected kiosks synchronously
                for (const tenantId of initialSelectedKiosks) {
                    await deleteResponder(editedEntry.id, tenantId);
                }

                for (const tenantId of selectedKiosks) {
                    const isAdmin = selectedKioskAdmins.has(tenantId);
                    const isVideoAccess = selectedVideoAccess.has(tenantId);
                    const isMovementAccess = selectedMovementNotifications.has(tenantId);
                    const isReviewVideoMessages = selectedReviewVideoMessages.has(tenantId);
                    const isOnlyAllowQRCodeUnlock = selectedOnlyAllowQRCodeUnlock.has(tenantId);
                    try {
                        await addResponder(editedEntry.id, tenantId, isAdmin, isVideoAccess, isMovementAccess, isReviewVideoMessages, isOnlyAllowQRCodeUnlock);
                    } catch (error) {
                        console.log(error);
                        setLoadingAddOrUpdate(false);
                        setErrorMessage(error.response.data);
                        setShowAlert(true);
                        errorOccurred = true;
                        break; // Exit the loop if an error occurs
                    }
                }
            }
            else
            {
                // delete all existing responders
                for (const tenant of kiosks) {
                    await deleteResponder(editedEntry.id, tenant.id);
                }

                await addAdminResponderToAllKiosks(editedEntry.id);
            }
            if (!errorOccurred) {
                setIsEditing(false);
                setLoadingAddOrUpdate(false);
                setDialogOpen(false);
                resetForm();
                fetchData();
            }
        } catch (error) {
            setLoadingAddOrUpdate(false);
            setErrorMessage(error.response.data);
            setShowAlert(true);
        }
    };


    const handleDelete = (id, isPartnerUser = false) => {
        if (isPartnerUser) {
            // Show confirmation dialog for partner users
            setUserToDelete(id);
            setPartnerDeleteDialogOpen(true);
        } else {
            // Proceed with normal deletion
            performDelete(id);
        }
    };

    const performDelete = (id) => {
        setDeletingIds(prevDeletingIds => {
            const newDeletingIds = new Set(prevDeletingIds);
            newDeletingIds.add(id);
            return newDeletingIds;
        });

        axios.delete(`/api/users/${id}`)
        .then(result => {
            setDeletingIds(prevDeletingIds => {
                const newDeletingIds = new Set(prevDeletingIds);
                newDeletingIds.delete(id);
                return newDeletingIds;
            });
            
            // Show different success messages based on user type
            if (result.data.isPartnerUser) {
                setSuccessMessage('Partner user deleted successfully. The partner will no longer be able to access your account.');
            } else {
                setSuccessMessage('User deleted successfully.');
            }
            setShowSuccess(true);
            
            // Reset the form and editing mode
            resetForm();
            // Refresh the list of users
            fetchData();
        })
        .catch(error => {
            setDeletingIds(prevDeletingIds => {
                const newDeletingIds = new Set(prevDeletingIds);
                newDeletingIds.delete(id);
                return newDeletingIds;
            });
            setErrorMessage('Error deleting user - it may be in use');
            setShowAlert(true);
            console.error('Error deleting user:', error)
        });
    };

    const handlePartnerDeleteConfirm = () => {
        setPartnerDeleteDialogOpen(false);
        performDelete(userToDelete);
        setUserToDelete(null);
    };

    const handlePartnerDeleteCancel = () => {
        setPartnerDeleteDialogOpen(false);
        setUserToDelete(null);
    };

    function resetForm()
    {
        setForm({ email: '', first_name: '', last_name: '', role: 'CLIENT_USER' });
        // clear all kiosk permissions
        setSelectedKiosks(new Set());
        setSelectedKioskAdmins(new Set());
        setSelectedVideoAccess(new Set());
        setSelectedMovementNotifications(new Set());
        setSelectedReviewVideoMessages(new Set());
        setSelectedOnlyAllowQRCodeUnlock(new Set());
        setInitialSelectedKiosks(new Set());
        setIsEditing(false);
        setEditedEntry(null);
    }

    // Function to handle dialog open/close for adding new users
    const handleDialogOpen = async () => {
        await fetchTenants();
        // Clear all selections for new user
        setSelectedKiosks(new Set());
        setSelectedKioskAdmins(new Set());
        setSelectedVideoAccess(new Set());
        setSelectedMovementNotifications(new Set());
        setSelectedReviewVideoMessages(new Set());
        setSelectedOnlyAllowQRCodeUnlock(new Set());
        setKioskSelectionError('');
        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        resetForm();
    };
    const columns = [
        // 100% of available space
        { 
            field: "email", 
            headerName: "Email", 
            flex: 1,
            renderCell: (params) => (
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography>{params.value}</Typography>
                    {params.row.is_partner_user && (
                        <Tooltip title="This is a partner account that was created when a partner set up your account. Partner accounts have access to all configuration settings but cannot see tenant snapshots or movement videos.">
                            <Chip 
                                icon={<BusinessIcon />}
                                label="Partner"
                                size="small"
                                color="info"
                                variant="outlined"
                            />
                        </Tooltip>
                    )}
                </Box>
            )
        },
        { field: "first_name", headerName: "First name", flex: 1},
        { field: "last_name", headerName: "Last name", flex: 1},
        { field: 'role', headerName: 'Role', width: 150},
        {
            field: "edit",
            headerName: "",
            width: 100,
            renderCell: (params) => (
                <Button 
                    disabled={ params.row.is_partner_user && !user.is_partner_user }
                    onClick={() => handleEditClick(params.row)} 
                    variant="contained" 
                    color="primary"
                >
                    Edit
                </Button>
            )
        },
        { field: 'delete', headerName: '', width: 100,        
            renderCell: (params) => (
            <Button
                onClick={() => handleDelete(params.id, params.row.is_partner_user)}
                variant="outlined"
                color="secondary"
                disabled={deletingIds.has(params.id) || params.id === user.id}
            >
                {deletingIds.has(params.id) ? <CircularProgress size={24} /> : 'Delete'}
            </Button>

            )
        },
    ];

    return (
        <Layout title='Users'> 
            <Box my={4}>
                <Box sx={{ 
                    mb: 2, 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Typography variant="body2">
                        Note - users are global to all kiosks
                    </Typography>
                    {!isMobile && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleDialogOpen}
                        >
                            Add User
                        </Button>
                    )}
                </Box>

                {isMobile ? (
                    <>
                        <Box sx={{ mb: 2 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleDialogOpen}
                                fullWidth
                            >
                                Add User
                            </Button>
                        </Box>
                        <Stack spacing={2}>
                            {rows.map((row) => (
                                <Card key={row.id}>
                                    <CardContent>
                                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                                            <Typography variant="h6">
                                                {row.first_name} {row.last_name}
                                            </Typography>
                                            {row.is_partner_user && (
                                                <Tooltip title="This is a partner account that was created when a partner set up your account. Partner accounts have access to all configuration settings but cannot see tenant snapshots or movement videos.">
                                                    <Chip 
                                                        icon={<BusinessIcon />}
                                                        label="Partner"
                                                        size="small"
                                                        color="info"
                                                        variant="outlined"
                                                    />
                                                </Tooltip>
                                            )}
                                        </Box>
                                        <Typography variant="body1" color="text.secondary" gutterBottom>
                                            {row.email}
                                        </Typography>
                                        <Typography variant="body2" gutterBottom>
                                            Role: {row.role === 'CLIENT_ADMIN' ? 'Admin' : 'User'}
                                        </Typography>
                                        
                                        <Stack 
                                            direction={{ xs: 'column', sm: 'row' }} 
                                            spacing={2} 
                                            sx={{ mt: 2 }}
                                        >
                                            <Button 
                                                onClick={() => handleEditClick(row)} 
                                                variant="contained" 
                                                color="primary"
                                                disabled={row.is_partner_user && !user.is_partner_user}
                                                fullWidth
                                            >
                                                Edit
                                            </Button>
                                            <Button
                                                onClick={() => handleDelete(row.id, row.is_partner_user)}
                                                variant="outlined"
                                                color="secondary"
                                                disabled={deletingIds.has(row.id) || row.id === user.id}
                                                fullWidth
                                            >
                                                {deletingIds.has(row.id) ? <CircularProgress size={24} /> : 'Delete'}
                                            </Button>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            ))}
                        </Stack>
                    </>
                ) : (
                    <Box sx={{ height: gridHeight }}>
                        <DataGrid 
                            rows={rows} 
                            columns={columns} 
                            loading={gridLoading}
                            pageSize={5} 
                            components={{
                                Header: {
                                    cell: (params) => <strong>{params.value}</strong>
                                }
                            }}
                        />
                    </Box>
                )}
            </Box>

            {/* MUI Dialog for adding or editing user */}
            <Dialog open={dialogOpen} onClose={handleDialogClose} aria-labelledby="form-dialog-title" maxWidth='lg'>
                <DialogTitle id="form-dialog-title">{isEditing ? 'Edit User' : 'Add User'}</DialogTitle>
                <DialogContent>
                    {  !isEditing && (
                        <Box mb={2}>
                            <Typography variant="body1" gutterBottom>
                                After adding a new user, they will receive an email with a link to set their password.
                            </Typography>
                            <Alert severity="info" sx={{ mt: 1 }}>
                                <Typography variant="body2">
                                    <strong>Security Notice:</strong> All existing Admin users will be automatically notified via email when you add this new user for security and transparency purposes.
                                </Typography>
                            </Alert>
                        </Box>
                    ) }
                    
                    <form onSubmit={isEditing ? handleUpdateSubmit : handleAddSubmit}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    label="Email"
                                    name="email"
                                    value={form.email}
                                    onChange={handleInputChange}
                                    variant="outlined"
                                    margin="normal"
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    label="First name"
                                    name="first_name"
                                    value={form.first_name}
                                    onChange={handleInputChange}
                                    variant="outlined"
                                    margin="normal"
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    label="Last name"
                                    name="last_name"
                                    value={form.last_name}
                                    onChange={handleInputChange}
                                    variant="outlined"
                                    margin="normal"
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControl fullWidth margin='normal'>
                                    <Grid container alignItems='center'>
                                        <Grid item xs={10}>
                                            <InputLabel id="role-label">Role</InputLabel>
                                            <Select
                                                fullWidth
                                                labelId='role-label'
                                                label="Role"
                                                name="role"
                                                value={form.role}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                {roleOptions.map(option => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </Grid>
                                        <Grid item xs={2}>
                                            <Tooltip title='Admin users have full access to all devices, billing, upgrades and user management. Regular Users only have the specific access to devices that you give them on this screen.'>
                                                <HelpIcon />
                                            </Tooltip> 
                                        </Grid>
                                    </Grid>                                   
                                </FormControl>
                            </Grid>
                        </Grid>
                        
                        {
                            form.role == 'CLIENT_USER' && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                App access to Device/kiosks
                                <Tooltip title="The selected devices and kiosks below are 'Responders' meaning they can login to the app, view the snapshot of the device, access the camera and respond to incoming calls. Additionally with admin access, the user can access both the portal and have access within the app to change device settings. Ad-hoc video access means they can connect to video on demand.">
                                    <IconButton>
                                        <HelpIcon/>
                                    </IconButton>
                                </Tooltip>
                            </Typography>

                            {kiosks.map((kiosk) => (
                                <Accordion key={kiosk.id} sx={{ mb: 1 }}>
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        sx={{ 
                                            backgroundColor: selectedKiosks.has(kiosk.id) ? 'action.selected' : 'inherit',
                                            '&:hover': { backgroundColor: 'action.hover' }
                                        }}
                                    >
                                        <Typography>{kiosk.OrganisationName}</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Stack spacing={1}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={selectedKiosks.has(kiosk.id)}
                                                        onChange={handleKioskCheckboxChange}
                                                        name={kiosk.id.toString()}
                                                    />
                                                }
                                                label="Responder (can login to app and respond to calls)"
                                                sx={{ ml: 0 }}
                                            />
                                            
                                            {selectedKiosks.has(kiosk.id) && (
                                                <>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={selectedKioskAdmins.has(kiosk.id)}
                                                                onChange={handleKioskAdminCheckboxChange}
                                                                name={`admin-${kiosk.id}`}
                                                                disabled={selectedOnlyAllowQRCodeUnlock.has(kiosk.id)}
                                                            />
                                                        }
                                                        label="Admin (can access portal and change device settings)"
                                                        sx={{ ml: 2 }}
                                                    />
                                                    
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={selectedKioskAdmins.has(kiosk.id) || selectedVideoAccess.has(kiosk.id)}
                                                                onChange={handleVideAccessCheckboxChange}
                                                                disabled={selectedKioskAdmins.has(kiosk.id) || selectedOnlyAllowQRCodeUnlock.has(kiosk.id)}
                                                                name={`video-${kiosk.id}`}
                                                            />
                                                        }
                                                        label="Allow Adhoc Video"
                                                        sx={{ ml: 2 }}
                                                    />
                                                    
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={selectedKioskAdmins.has(kiosk.id) || selectedMovementNotifications.has(kiosk.id)}
                                                                onChange={handleMovementNotificationsCheckboxChange}
                                                                disabled={selectedKioskAdmins.has(kiosk.id) || selectedOnlyAllowQRCodeUnlock.has(kiosk.id)}
                                                                name={`movement-${kiosk.id}`}
                                                            />
                                                        }
                                                        label="Receive Movement Notifications"
                                                        sx={{ ml: 2 }}
                                                    />
                                                    
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={selectedKioskAdmins.has(kiosk.id) || selectedReviewVideoMessages.has(kiosk.id)}
                                                                onChange={handleReviewVideoMessagesCheckboxChange}
                                                                disabled={selectedKioskAdmins.has(kiosk.id) || selectedOnlyAllowQRCodeUnlock.has(kiosk.id)}
                                                                name={`reviewvideomessages-${kiosk.id}`}
                                                            />
                                                        }
                                                        label="Review Video Messages"
                                                        sx={{ ml: 2 }}
                                                    />
                                                    
                                                    {!selectedKioskAdmins.has(kiosk.id) && (
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={selectedOnlyAllowQRCodeUnlock.has(kiosk.id)}
                                                                    onChange={handleOnlyAllowQRCodeUnlockCheckboxChange}
                                                                    name={`onlyallowqrcodeunlock-${kiosk.id}`}
                                                                />
                                                            }
                                                            label="Only Allow QR Code Unlock"
                                                            sx={{ ml: 2 }}
                                                        />
                                                    )}
                                                </>
                                            )}
                                        </Stack>
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </Box>
                        )
                    }
                    <DialogActions>
                    { kioskSelectionError && (
                                <Alert severity="error">{kioskSelectionError}</Alert>
                            )}
                        <Button 
                            onClick={(e) => {
                                e.preventDefault();  // Prevent form submission
                                handleDialogClose();
                            }} 
                            color="primary" 
                            variant='contained'
                            type="button"  // Explicitly set type to button
                        >
                            Cancel
                        </Button>
                        <Button 
                            type="submit" 
                            color="primary" 
                            variant='contained' 
                            disabled={loadingAddOrUpdate}
                        >
                            {isEditing ? 
                                ( loadingAddOrUpdate ? <CircularProgress size={24} /> : 'Update') : 
                                ( loadingAddOrUpdate ? <CircularProgress size={24} /> : 'Add')
                            }
                        </Button>
                    </DialogActions>
                    
                    </form>
                    
                </DialogContent>
            </Dialog>

            {showAlert && (
                <Snackbar
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                open={showAlert}
                autoHideDuration={10000}
                message={errorMessage}
                onClose={() => setShowAlert(false)}
                >
                    <Alert severity="error" onClose={() => setShowAlert(false)}>
                    {errorMessage}
                    </Alert>
                </Snackbar>
            )}
                        
            {showSuccess && (
                <Snackbar
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                open={showSuccess}
                autoHideDuration={5000}
                message={successMessage}
                onClose={() => setShowSuccess(false)}
                >
                    <Alert severity="success" onClose={() => setShowSuccess(false)}>
                    {successMessage}
                    </Alert>
                </Snackbar>
            )}

            {/* Partner User Deletion Confirmation Dialog */}
            <Dialog 
                open={partnerDeleteDialogOpen} 
                onClose={handlePartnerDeleteCancel}
                aria-labelledby="partner-delete-dialog-title"
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle id="partner-delete-dialog-title">
                    <Box display="flex" alignItems="center" gap={1}>
                        <BusinessIcon color="warning" />
                        <Typography variant="h6">Delete Partner User</Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="body2" gutterBottom>
                            <strong>Important:</strong> You are about to delete a partner user account.
                        </Typography>
                        <Typography variant="body2">
                            By deleting this account, the partner will no longer be able to access your account 
                            or provide support. This action cannot be undone.
                        </Typography>
                    </Alert>
                    <Typography variant="body1">
                        Are you sure you want to proceed with deleting this partner user?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={handlePartnerDeleteCancel} 
                        color="primary" 
                        variant="outlined"
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handlePartnerDeleteConfirm} 
                        color="error" 
                        variant="contained"
                    >
                        Delete Partner User
                    </Button>
                </DialogActions>
            </Dialog>

        </Layout>
    );
}

export default UsersGrid;
